/**
 * /el/guides/ilektroniko-prosklitirio-ti-na-grafei
 *
 * The Greek counterpart of `what-to-include-digital-wedding-invitation.en.js`.
 * An ADAPTATION — the differences below are all deliberate.
 *
 * ── «Ηλεκτρονικό», not «ψηφιακό» ──────────────────────────────────────────
 * The research found «ηλεκτρονικό προσκλητήριο» to be the term Greek couples
 * search with; «ψηφιακό» trails it. The site's approved product page keeps its
 * own wording and is NOT changed to match — a guide targets the query, a
 * product page keeps its approved copy, and the two are allowed to differ.
 *
 * The Greek market also uses «ηλεκτρονικό προσκλητήριο» for two different
 * things: an image or video file that is sent, and a page a guest opens and
 * replies on. Section two names that distinction in four sentences and moves
 * on. It must not grow: `wedding-website-vs-digital-wedding-invitation` is a
 * separate English guide with no Greek counterpart in this cohort, and this
 * article is not it.
 *
 * ── Greek wedding conventions, not Anglophone ones ────────────────────────
 * Το μυστήριο and η δεξίωση are two places and often two times, which is why
 * the "where" section is built around both rather than around a venue and a
 * reception line. Οι κουμπάροι are named where the English article says "the
 * wedding party". Οι γονείς appear in the hosts question, which in Greece is
 * still frequently theirs. The printed-suite structure the English article
 * argues against — invitation card, RSVP card, details card, envelope
 * addressing — is not described at all here, because the Greek reader has no
 * reason to have it in mind.
 *
 * ── The rule the article is written under ─────────────────────────────────
 * NO ETIQUETTE LAW. Inherited whole. Every recommendation is conditional
 * («συνήθως», «στους περισσότερους γάμους», «αν χρειάζεται»), and the
 * three-level model is a way to decide, not a specification. The two places
 * where legislating is most tempting and this article deliberately does not:
 * who hosts (a family question, not a formatting one), and children and
 * companions (no policy is presented as correct — what matters is that a guest
 * can TELL).
 *
 * ── The claims this guide is allowed to make about Agapaé ─────────────────
 * Identical to the English module's, verified in the same places. What the
 * invitation actually carries: the couple's names, the date, the church and the
 * venue with their map links, the schedule of the day (time, title, optional
 * description), the couple's story, photographs, gift information (a note, bank
 * details, or a link to a registry the couple already keeps), the couple's
 * phone numbers shown with the RSVP, the RSVP deadline, and the RSVP itself —
 * answered per person and per moment.
 *
 * What must NOT be claimed, and each is a live risk here because the article
 * recommends things the product has no field for:
 *
 *   - NO dress-code field. The article recommends a dress code; the invitation
 *     has no dedicated place for one. Do not imply otherwise.
 *   - NO travel, accommodation, parking, accessibility or FAQ sections.
 *   - NO registry hosting, gift list or payment processing.
 *   - NOT a website builder, and NO template gallery: one editorial design, in
 *     Greek and English.
 *   - NO custom domain or chosen URL — the link is an auto-generated event id,
 *     so «ο δικός του σύνδεσμος» is the ceiling.
 *   - NO «απεριόριστα», «για πάντα», «μόνιμα» anywhere.
 *
 * Invitation is Heirloom-only (`planFeatures.mjs`), stated in the note.
 */

const whatToIncludeDigitalWeddingInvitationEl = {
  slug: "ilektroniko-prosklitirio-ti-na-grafei",

  title: "Τι πρέπει να γράφει το ηλεκτρονικό προσκλητήριο γάμου",
  metaTitle: "Τι πρέπει να γράφει το ηλεκτρονικό προσκλητήριο γάμου | Agapaé",
  metaDescription:
    "Τι πρέπει να γράφει ένα ηλεκτρονικό προσκλητήριο γάμου: ονόματα, ημερομηνία, εκκλησία και δεξίωση, RSVP, dress code, συνοδοί και παιδιά, δώρα και πρόσβαση.",

  eyebrow: "Προσκλητήριο",

  hero: {
    // The same photograph the English guide carries, described in Greek.
    src: "/assets/images/lovely_couple_004.jpeg",
    alt: "Ασπρόμαυρο πορτρέτο νύφης σε προφίλ, με το πρόσωπό της να φαίνεται μέσα από διάφανο πέπλο και μικρά θολά φώτα στο σκοτάδι πίσω της",
    width: 1280,
    height: 714,
  },

  intro: [
    `Οι καλεσμένοι σας θα ανοίξουν το προσκλητήριο στο κινητό, μία φορά, πιθανότατα ενώ κάνουν κάτι άλλο.`,
    `Εκείνη τη στιγμή ψάχνουν λίγα πράγματα: ποιανού είναι ο γάμος, πότε, πού, και τι τους ζητάτε να κάνουν.`,
    `Όλα τα υπόλοιπα μπορούν να περιμένουν.`,
    `Το ηλεκτρονικό προσκλητήριο έχει ένα πλεονέκτημα και μία παγίδα, και είναι το ίδιο πράγμα: χωράει τα πάντα. Σε ένα τυπωμένο προσκλητήριο κάθε γραμμή κοστίζει χαρτί και χώρο, και αυτό από μόνο του βάζει τάξη. Στην οθόνη δεν κοστίζει τίποτα, οπότε την τάξη πρέπει να τη βάλετε εσείς.`,
    `Ο πιο χρήσιμος τρόπος να το σκεφτείτε είναι σε τρία επίπεδα: τα απαραίτητα, τα χρήσιμα και όσα αφορούν κάποιους μόνο.`,
    `Δείτε τι μπαίνει σε κάθε επίπεδο και πού ακριβώς.`,
  ],

  sections: [
    {
      heading: "Τι πρέπει να γράφει ένα ηλεκτρονικό προσκλητήριο",
      content: [
        `Τα απαραίτητα είναι λίγα και δεν αλλάζουν:`,
        {
          type: "list",
          items: [
            `τα ονόματά σας`,
            `την ημερομηνία`,
            `την ώρα`,
            `το πού γίνεται το μυστήριο`,
            `το πού συνεχίζει η δεξίωση`,
            `τι τους ζητάτε να απαντήσουν, και μέχρι πότε`,
          ],
        },
        `Αν ένας καλεσμένος δει μόνο αυτά, ξέρει ήδη αν μπορεί να έρθει και τι πρέπει να κάνει.`,
        `Τα χρήσιμα είναι όσα θα τα ψάξει σχεδόν σίγουρα: πώς να φτάσει, τι να φορέσει, πού να παρκάρει.`,
        `Τα υπόλοιπα αφορούν κάποιους μόνο — διαμονή, μετακίνηση από άλλη πόλη, ειδικές διευκολύνσεις — και ανήκουν πιο βαθιά, όχι στην πρώτη οθόνη.`,
        `Η αρχή είναι απλή: το ότι χωράει κάτι δεν σημαίνει ότι πρέπει να μπει μπροστά.`,
      ],
    },
    {
      heading: "Αρχείο ή σελίδα; Δύο πράγματα με το ίδιο όνομα",
      content: [
        `Στην Ελλάδα «ηλεκτρονικό προσκλητήριο» λέγονται δύο διαφορετικά πράγματα.`,
        `Το ένα είναι ένα αρχείο — μια εικόνα ή ένα βίντεο — που το στέλνετε στους καλεσμένους.`,
        `Το άλλο είναι μια σελίδα που ανοίγει ο καλεσμένος, διαβάζει και απαντά μέσα από αυτήν.`,
        `Ο οδηγός αυτός αφορά κυρίως το δεύτερο, γιατί μόνο εκεί έχει νόημα η ερώτηση «τι μπαίνει πού»: ένα αρχείο δείχνει όσα χωράει σε μία εικόνα, ενώ μια σελίδα σας αναγκάζει να αποφασίσετε τι έρχεται πρώτο.`,
      ],
    },
    {
      heading: "Ξεκινήστε από τα ονόματα",
      content: [
        `Το πρώτο πράγμα που πρέπει να καταλάβει κάποιος είναι ποιανού είναι ο γάμος.`,
        `Ακούγεται αυτονόητο, αλλά χάνεται συχνά κάτω από ένα σχέδιο, μια φωτογραφία ή μια φράση που μπήκε πρώτη.`,
        `Γράψτε τα ονόματά σας όπως σας ξέρουν οι άνθρωποι που καλείτε. Ένας θείος που ξέρει το χαϊδευτικό σας δεν χρειάζεται το πλήρες όνομα ταυτότητας.`,
        `Το αν θα αναφέρετε τους γονείς εξαρτάται από την οικογένειά σας και από το ποιος «καλεί» τυπικά στον γάμο. Σε πολλούς ελληνικούς γάμους αυτό εξακολουθεί να είναι δουλειά των γονιών, σε άλλους όχι.`,
        `Δεν υπάρχει σωστή απάντηση. Υπάρχει η δική σας.`,
      ],
    },
    {
      heading: "Κάντε την ημερομηνία και την ώρα αδιαμφισβήτητες",
      content: [
        `Γράψτε την ημέρα της εβδομάδας μαζί με την ημερομηνία. Είναι η πιο φθηνή διόρθωση λάθους που υπάρχει.`,
        `Δηλώστε την ώρα που πρέπει να είναι εκεί ο καλεσμένος, όχι την ώρα που ξεκινάτε εσείς. Αν το μυστήριο αρχίζει στις 6, οι καλεσμένοι πρέπει να είναι στις 5:45, και το προσκλητήριο είναι το σωστό μέρος για να το πείτε.`,
        `Αν το μυστήριο και η δεξίωση έχουν διαφορετικές ώρες, δώστε και τις δύο. Το κενό ανάμεσά τους είναι από τα πιο συχνά ερωτήματα που φτάνουν στο ζευγάρι με μήνυμα.`,
        `Και προσοχή στη χρονιά. Ένα προσκλητήριο που στέλνεται τον Δεκέμβριο για γάμο τον Ιούνιο διαβάζεται σε δύο διαφορετικά χρόνια.`,
      ],
    },
    {
      heading: "Πού: το μυστήριο και η δεξίωση",
      content: [
        `Ο καλεσμένος χρειάζεται δύο πληροφορίες για κάθε σημείο: πώς λέγεται και πώς φτάνει.`,
        `Γράψτε το όνομα του ναού ή του χώρου όπως αναγράφεται και όπως θα το βρει κανείς σε έναν χάρτη — όχι μόνο όπως το λέτε μεταξύ σας.`,
        `Βάλτε σύνδεσμο χάρτη για κάθε σημείο ξεχωριστά. Είναι το πιο χρήσιμο πράγμα που μπορεί να κάνει ένα ηλεκτρονικό προσκλητήριο και δεν μπορεί να κάνει ένα τυπωμένο.`,
        `Αν τα δύο σημεία απέχουν, πείτε το. Μια πρόταση αρκεί: πόσο περίπου παίρνει η μετακίνηση και αν χρειάζεται αυτοκίνητο.`,
        {
          type: "note",
          text: `Πολλά εξοχικά κτήματα εμφανίζονται λάθος ή διπλά στους χάρτες. Ανοίξτε τον σύνδεσμο που θα βάλετε από κινητό, σαν να μην έχετε ξαναπάει.`,
        },
      ],
    },
    {
      heading: "Εξηγήστε τι θα γίνει",
      content: [
        `Οι καλεσμένοι θέλουν να ξέρουν πώς κυλάει η μέρα — όχι λεπτό προς λεπτό, αλλά σε γενικές γραμμές.`,
        `Λίγες γραμμές αρκούν: μυστήριο, δεξίωση, φαγητό, χορός.`,
        `Αυτό απαντά ερωτήσεις που κανείς δεν θα σας κάνει ευθέως: αν θα φάνε, πόσο θα κρατήσει, αν βγάζει νόημα να έρθουν με παιδί, αν προλαβαίνουν το τελευταίο δρομολόγιο.`,
        `Κρατήστε το σύντομο. Ένα αναλυτικό πρόγραμμα είναι για εσάς και τους συνεργάτες σας, όχι για τους καλεσμένους.`,
      ],
    },
    {
      heading: "Κάντε το RSVP απλό",
      content: [
        `Αν ζητάτε απάντηση, ζητήστε τη μία φορά και ξεκάθαρα.`,
        `Πείτε τρία πράγματα: ότι θέλετε απάντηση, μέχρι πότε, και πώς δίνεται.`,
        `Η προθεσμία δεν είναι τυπικότητα. Είναι η μέρα από την οποία εξαρτώνται ο χώρος, το φαγητό και τα τραπέζια. Στους περισσότερους γάμους μπαίνει τρεις με τέσσερις εβδομάδες πριν, αλλά ρωτήστε τον χώρο σας — κάποιοι θέλουν τους τελικούς αριθμούς νωρίτερα.`,
        `Αν η απάντηση δίνεται μέσα από το ίδιο το προσκλητήριο, το πιο σημαντικό είναι να είναι εμφανής. Ένα κουμπί που φαίνεται χωρίς κύλιση πατιέται πολύ πιο συχνά από ένα που κρύβεται στο τέλος.`,
        `Και αφήστε έναν τρόπο να σας βρουν αλλιώς. Πάντα θα υπάρχει κάποιος που θα προτιμήσει να τηλεφωνήσει.`,
      ],
    },
    {
      heading: "Οι πληροφορίες που θα χρειαστούν πραγματικά",
      content: [
        `Τρία πράγματα ψάχνουν σχεδόν όλοι, και αν δεν τα βρουν θα σας ρωτήσουν:`,
        {
          type: "list",
          items: [
            `τι να φορέσουν`,
            `πού να παρκάρουν`,
            `πώς φτάνουν αν δεν έχουν αυτοκίνητο`,
          ],
        },
        `Το dress code δεν χρειάζεται να είναι επίσημη οδηγία. Μια φράση σε φυσικό λόγο λέει περισσότερα από έναν όρο πεταμένο μόνο του, ειδικά αν ο γάμος γίνεται σε παραλία, σε κτήμα με χώμα ή τον Αύγουστο.`,
        `Για το παρκάρισμα, μια πρόταση αρκεί — αν υπάρχει χώρος, αν είναι περιορισμένος, αν υπάρχει εναλλακτική.`,
        `Αυτά ανήκουν στο δεύτερο επίπεδο: όχι στην πρώτη οθόνη, αλλά κάπου που θα τα βρει εύκολα κανείς.`,
      ],
    },
    {
      heading: "Πότε μπαίνουν διαμονή και μετακίνηση",
      content: [
        `Όταν αφορούν αρκετούς καλεσμένους ώστε να αξίζει.`,
        `Σε έναν γάμο όπου σχεδόν όλοι μένουν κοντά, μια ενότητα για ξενοδοχεία είναι θόρυβος. Σε έναν γάμο σε νησί ή σε χωριό, είναι από τα πιο χρήσιμα πράγματα στο προσκλητήριο.`,
        `Αν τη βάλετε, κρατήστε τη πρακτική: δύο ή τρεις επιλογές, την περιοχή, και ό,τι έχετε κλείσει εσείς.`,
        `Αυτά είναι πληροφορίες τρίτου επιπέδου. Δεν πρέπει να στέκονται ανάμεσα στον καλεσμένο και στην απάντησή του.`,
      ],
    },
    {
      heading: "Συνοδοί και παιδιά",
      content: [
        `Είναι το σημείο που δημιουργεί τις περισσότερες παρεξηγήσεις, και σχεδόν πάντα επειδή δεν ειπώθηκε τίποτα.`,
        `Δεν υπάρχει σωστή πολιτική. Υπάρχει η δική σας, και το ζητούμενο είναι να μπορεί ο καλεσμένος να καταλάβει ποια είναι — χωρίς να χρειαστεί να ρωτήσει.`,
        `Αν κάποιος καλείται με συνοδό, φροντίστε να το βλέπει. Αν τα παιδιά είναι ευπρόσδεκτα, πείτε το· είναι πληροφορία που αλλάζει τα σχέδια μιας ολόκληρης οικογένειας. Αν ο γάμος είναι μόνο για ενήλικες, πείτε το ήπια και μία φορά.`,
        `Ένα ηλεκτρονικό προσκλητήριο έχει εδώ ένα πλεονέκτημα: αν κάθε καλεσμένος ανοίγει τη δική του πρόσκληση, η απάντηση μπορεί να είναι συγκεκριμένη για αυτόν αντί για έναν γενικό κανόνα που ισχύει για όλους.`,
      ],
    },
    {
      heading: "Τα δώρα, με διακριτικότητα",
      content: [
        `Οι καλεσμένοι θα το αναζητήσουν. Είναι από τα πρώτα πράγματα που ρωτάει κανείς μεταξύ τους.`,
        `Ταυτόχρονα δεν είναι λόγος να έρθουν, και το προσκλητήριο δεν πρέπει να διαβάζεται σαν να είναι.`,
        `Ο κανόνας που δουλεύει: χαμηλά στη σελίδα, σύντομα, και μόνο αν το θέλετε. Μία ή δύο προτάσεις, χωρίς να τους ζητάτε τίποτα.`,
        `Ό,τι κι αν επιλέξετε — μια σημείωση, έναν τραπεζικό λογαριασμό, έναν σύνδεσμο σε λίστα που ήδη κρατάτε αλλού — μην το βάλετε δίπλα στο RSVP. Είναι δύο εντελώς διαφορετικές ερωτήσεις προς τον καλεσμένο.`,
      ],
    },
    {
      heading: "Όταν η μέρα έχει περισσότερες από μία στιγμές",
      content: [
        `Πολλοί γάμοι δεν εξαντλούνται σε μία εκδήλωση. Μπορεί να υπάρχει κάτι την παραμονή, μια δεξίωση που συνεχίζει αλλού, ή ένα τραπέζι την επόμενη μέρα για όσους ήρθαν από μακριά.`,
        `Όταν συμβαίνει αυτό, δύο πράγματα πρέπει να είναι σαφή: ποιος καλείται σε τι, και σε τι απαντάει ο καθένας.`,
        `Μια λίστα με τρεις στιγμές όπου δεν ξεχωρίζει ποια αφορά ποιον δημιουργεί περισσότερα μηνύματα από όσα γλιτώνει.`,
        `Αν οι καλεσμένοι μπορούν να απαντήσουν χωριστά για κάθε στιγμή, θα ξέρετε πραγματικά πόσοι έρχονται πού — αντί να το υπολογίζετε.`,
      ],
    },
    {
      heading: "Κρατήστε το άνοιγμα απλό",
      content: [
        `Η πρώτη οθόνη έχει μία δουλειά: να πει ποιανού είναι ο γάμος και πότε.`,
        `Μια φωτογραφία, τα ονόματα, η ημερομηνία. Ίσως μία φράση δική σας.`,
        `Ό,τι μπαίνει πριν από αυτά — μια μεγάλη εισαγωγή, ένα βίντεο που ξεκινά μόνο του, μια σελίδα υποδοχής — καθυστερεί τον καλεσμένο σε σχέση με αυτό που ήρθε να δει.`,
        `Η ιστορία σας, οι φωτογραφίες και οι λεπτομέρειες έχουν τη θέση τους. Απλώς όχι πριν από την πληροφορία.`,
      ],
    },
    {
      heading: "Μια σύντομη λίστα ελέγχου",
      content: [
        `Πριν το στείλετε, περάστε τα τρία επίπεδα:`,
        {
          type: "steps",
          items: [
            `Απαραίτητα: ονόματα, ημερομηνία, ώρα, μυστήριο, δεξίωση, RSVP με προθεσμία.`,
            `Χρήσιμα: χάρτες, dress code, παρκάρισμα, η ροή της ημέρας, τρόπος επικοινωνίας.`,
            `Κατά περίπτωση: διαμονή, μετακίνηση, δώρα, επιπλέον στιγμές, διευκολύνσεις.`,
          ],
        },
        `Αν κάτι δεν ανήκει σε κανένα από τα τρία, πιθανότατα δεν ανήκει στο προσκλητήριο.`,
      ],
    },
    {
      heading: "Πριν το στείλετε, δείτε το σαν καλεσμένος",
      content: [
        `Ανοίξτε το στο κινητό, όχι στον υπολογιστή. Εκεί θα το δουν σχεδόν όλοι.`,
        `Και ελέγξτε:`,
        {
          type: "list",
          items: [
            `Καταλαβαίνει κάποιος μέσα σε πέντε δευτερόλεπτα ποιανού είναι ο γάμος και πότε;`,
            `Φαίνεται το RSVP χωρίς να κατεβεί κανείς μέχρι το τέλος;`,
            `Ανοίγουν οι σύνδεσμοι των χαρτών και δείχνουν το σωστό σημείο;`,
            `Είναι ξεκάθαρο αν καλείται με συνοδό ή με παιδιά;`,
            `Υπάρχει τρόπος να σας βρει κάποιος που δεν θα απαντήσει ηλεκτρονικά;`,
          ],
        },
        `Δώστε το και σε δύο ανθρώπους πριν το στείλετε: κάποιον που δεν ξέρει λεπτομέρειες για τον γάμο σας, και κάποιον μεγαλύτερο. Θα βρουν διαφορετικά πράγματα.`,
      ],
    },
    {
      heading: "Το προσκλητήριο στο Agapaé",
      content: [
        `Στο Agapaé το προσκλητήριο είναι μια σελίδα με τον δικό της σύνδεσμο, στα ελληνικά ή στα αγγλικά.`,
        `Κρατά τα ονόματά σας, την ημερομηνία, την εκκλησία και τον χώρο της δεξίωσης με τους χάρτες τους, τη ροή της ημέρας — κάθε στιγμή με ώρα, τίτλο και μια προαιρετική γραμμή περιγραφής — την ιστορία σας, φωτογραφίες, και όσα θέλετε να πείτε για τα δώρα: μια σημείωση, τραπεζικά στοιχεία ή έναν σύνδεσμο σε λίστα που ήδη κρατάτε αλλού.`,
        `Το RSVP βρίσκεται μέσα στο ίδιο το προσκλητήριο, με την προθεσμία και τα τηλέφωνά σας δίπλα του. Οι καλεσμένοι απαντούν ανά άτομο και ανά στιγμή, οπότε ξέρετε ποιος έρχεται στο μυστήριο, ποιος στη δεξίωση και ποιος και στα δύο.`,
        `Ό,τι δεν έχει θέση εκεί — διαμονή, μετακίνηση, dress code — ανήκει όπου λέτε στους καλεσμένους τα υπόλοιπα. Ένα προσκλητήριο δεν χρειάζεται να τα χωρέσει όλα· χρειάζεται να είναι σαφές για όσα κρατά.`,
        {
          type: "note",
          text: `Το προσκλητήριο είναι διαθέσιμο στο πακέτο Heirloom.`,
        },
        {
          type: "p",
          text: [
            {
              text: "Δείτε το ψηφιακό προσκλητήριο στο Agapaé",
              href: "/digital-wedding-invitation",
            },
          ],
        },
      ],
    },
    {
      heading: "Τι κάνει ένα προσκλητήριο να μοιάζει ολοκληρωμένο",
      content: [
        `Όχι το πόσα λέει.`,
        `Ένα προσκλητήριο μοιάζει ολοκληρωμένο όταν ο καλεσμένος το κλείνει χωρίς απορία — ξέρει πού να πάει, πότε, τι να φορέσει και τι του ζητήσατε.`,
        `Περισσότερος χώρος για να οργανώσετε πληροφορία δεν είναι περισσότερος λόγος να τη βάλετε.`,
        `Βάλτε τα απαραίτητα μπροστά, τα χρήσιμα κοντά, τα υπόλοιπα πιο βαθιά.`,
        `Και αφήστε το να μοιάζει με πρόσκληση, όχι με φόρμα.`,
      ],
    },
  ],

  faqHeading: "Συχνές ερωτήσεις",

  faq: [
    {
      q: `Τι πρέπει να γράφει οπωσδήποτε ένα ηλεκτρονικό προσκλητήριο γάμου;`,
      a: `Τα ονόματά σας, την ημερομηνία και την ώρα, το πού γίνεται το μυστήριο, το πού συνεχίζει η δεξίωση, και τι ζητάτε από τους καλεσμένους — απάντηση, με προθεσμία. Όλα τα υπόλοιπα είναι χρήσιμα, όχι απαραίτητα.`,
    },
    {
      q: `Πρέπει το προσκλητήριο να έχει RSVP μέσα;`,
      a: `Βοηθάει πολύ, γιατί η απάντηση δίνεται τη στιγμή που ο καλεσμένος διαβάζει. Το σημαντικό είναι να φαίνεται χωρίς κύλιση και να υπάρχει και ένας εναλλακτικός τρόπος επικοινωνίας για όσους προτιμούν το τηλέφωνο.`,
    },
    {
      q: `Να γράψω dress code στο προσκλητήριο;`,
      a: `Αν έχετε κάποια προτίμηση, ναι — οι καλεσμένοι θα το ψάξουν ούτως ή άλλως. Μια φράση σε φυσικό λόγο λειτουργεί καλύτερα από μια επίσημη οδηγία, ειδικά αν ο γάμος γίνεται σε παραλία, σε κτήμα ή μέσα στο καλοκαίρι.`,
    },
    {
      q: `Πρέπει να βάλω πληροφορίες για διαμονή και μετακίνηση;`,
      a: `Μόνο αν αφορούν αρκετούς καλεσμένους. Σε γάμο όπου οι περισσότεροι μένουν κοντά είναι περιττές· σε γάμο σε νησί ή σε χωριό είναι από τα πιο χρήσιμα πράγματα που μπορείτε να δώσετε.`,
    },
    {
      q: `Πώς δηλώνω αν κάποιος καλείται με συνοδό ή με παιδιά;`,
      a: `Ρητά, και όσο πιο συγκεκριμένα γίνεται. Δεν υπάρχει σωστή πολιτική — υπάρχει η δική σας, και το ζητούμενο είναι να την καταλαβαίνει ο καλεσμένος χωρίς να χρειαστεί να ρωτήσει.`,
    },
  ],

  related: {
    heading: "Πού ταιριάζει αυτό στο Agapaé",

    // The rest of the Greek invitation cluster: the category question that
    // comes before this one, and the deadline the reply hangs on.
    guides: [
      "wedding-website-vs-digital-wedding-invitation",
      "when-should-wedding-guests-rsvp",
    ],

    product: [
      {
        href: "/digital-wedding-invitation",
        label: "Ψηφιακό προσκλητήριο",
        blurb:
          "Το προσκλητήριο του γάμου σας ως σελίδα με τον δικό της σύνδεσμο, με τις λεπτομέρειες της ημέρας και το RSVP μέσα σε αυτό.",
      },
      {
        href: "/wedding-rsvp",
        label: "RSVP γάμου",
        blurb:
          "Συγκεντρώστε τις απαντήσεις των καλεσμένων ανά άτομο και ανά στιγμή του γάμου, με προθεσμία και υπενθυμίσεις.",
      },
    ],
  },
};

export default whatToIncludeDigitalWeddingInvitationEl;
